import { useState, useCallback } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AddLinkIcon from "@mui/icons-material/AddLink";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { PageWithTitle } from "src/Common/components";
import { PairTable } from "./components/PairTable";
import { ManualPairDialog } from "./components/ManualPairDialog";
import { MatchingHistoryTab } from "./components/MatchingHistoryTab";
import {
  usePairs,
  usePairStats,
  useConfirmPair,
  useRejectPair,
  useRunMatching,
  useFetchMarkets,
} from "./api/usePairMapper";

const MATCH_TYPES = [
  { value: "", label: "All" },
  { value: "manual", label: "Manual" },
  { value: "ai_confirmed", label: "AI Confirmed" },
  { value: "ai_pending", label: "AI Pending" },
  { value: "keyword_pending", label: "Keyword Pending" },
];

const STATUSES = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "expired", label: "Expired" },
];

export const PredictionMapperPage = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState("");
  const [matchedBy, setMatchedBy] = useState("");
  const [search, setSearch] = useState("");
  const [manualPairOpen, setManualPairOpen] = useState(false);
  const [tab, setTab] = useState(0);

  const { data, isLoading } = usePairs({
    status: status || undefined,
    matchedBy: matchedBy || undefined,
    search: search || undefined,
    limit: pageSize,
    offset: page * pageSize,
  });

  const { data: stats } = usePairStats();
  const confirmMutation = useConfirmPair();
  const rejectMutation = useRejectPair();
  const runMatchingMutation = useRunMatching();
  const fullRunMutation = useRunMatching(true);
  const fetchMutation = useFetchMarkets();

  const handleConfirm = useCallback(
    (id: string) => confirmMutation.mutate(id),
    [confirmMutation],
  );

  const handleReject = useCallback(
    (id: string) => {
      if (confirm("Reject this pair? Markets will be unlinked and cannot be re-matched.")) {
        rejectMutation.mutate(id);
      }
    },
    [rejectMutation],
  );

  return (
    <PageWithTitle>
      <Box px={2} py={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ color: "white" }}>Prediction Pair Mapper</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddLinkIcon />}
              onClick={() => setManualPairOpen(true)}
            >
              Manual Pair
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                fetchMutation.isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CloudDownloadIcon />
                )
              }
              onClick={() => fetchMutation.mutate()}
              disabled={fetchMutation.isLoading || runMatchingMutation.isLoading || fullRunMutation.isLoading}
            >
              Fetch Markets
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={
                runMatchingMutation.isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PlayArrowIcon />
                )
              }
              onClick={() => runMatchingMutation.mutate()}
              disabled={runMatchingMutation.isLoading || fullRunMutation.isLoading}
            >
              Run Matching
            </Button>
            <Button
              variant="contained"
              size="small"
              color="warning"
              startIcon={
                fullRunMutation.isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PlayArrowIcon />
                )
              }
              onClick={() => {
                if (confirm("Full run: fetch all markets, re-extract keywords, match all unpaired. This may take a while. Continue?")) {
                  fullRunMutation.mutate();
                }
              }}
              disabled={runMatchingMutation.isLoading || fullRunMutation.isLoading}
            >
              Full Run
            </Button>
          </Stack>
        </Stack>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2, borderBottom: "1px solid rgba(255,255,255,0.1)" }}
        >
          <Tab label="Pairs" sx={{ textTransform: "none" }} />
          <Tab label="Matching History" sx={{ textTransform: "none" }} />
        </Tabs>

        {tab === 0 && (
          <>
            {stats && (
              <Stack direction="row" spacing={1} mb={2}>
                <Chip label={`Total: ${stats.total}`} size="small" />
                <Chip label={`Active: ${stats.active}`} size="small" color="success" />
                <Chip
                  label={`AI Pending: ${stats.pending}`}
                  size="small"
                  color="warning"
                />
                <Chip
                  label={`KW Pending: ${stats.keywordPending}`}
                  size="small"
                  color="warning"
                />
                <Chip
                  label={`Expired: ${stats.expired}`}
                  size="small"
                  color="error"
                />
              </Stack>
            )}

            <Stack direction="row" spacing={2} mb={2}>
              <TextField
                size="small"
                label="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                sx={{ width: 250 }}
              />
              <TextField
                size="small"
                label="Status"
                select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(0);
                }}
                sx={{ width: 140 }}
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Match Type"
                select
                value={matchedBy}
                onChange={(e) => {
                  setMatchedBy(e.target.value);
                  setPage(0);
                }}
                sx={{ width: 180 }}
              >
                {MATCH_TYPES.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <PairTable
              pairs={data?.pairs || []}
              total={data?.total || 0}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
              }}
              isLoading={isLoading}
              onConfirm={handleConfirm}
              onReject={handleReject}
            />
          </>
        )}

        {tab === 1 && <MatchingHistoryTab />}
      </Box>
      <ManualPairDialog
        open={manualPairOpen}
        onClose={() => setManualPairOpen(false)}
      />
    </PageWithTitle>
  );
};
